import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import PostCard from '../components/PostCard';
import Marketplace from '../components/Marketplace';
import Lightbox from '../components/Lightbox';
import SettingsModal from '../components/SettingsModal';
import ProfileModal from '../components/ProfileModal';
import PublicStorefront from '../components/PublicStorefront';
import MobileDock from '../components/MobileDock';
import SocialFeed from '../components/SocialFeed';
import PrivateChat from '../components/PrivateChat';
import NotificationCenter from '../components/NotificationCenter';
import { userPictureUrl } from '@/lib/media';
import { BACKEND_URL } from '@/lib/config';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeAndroid } from '@/lib/native';

const UploadModal = React.lazy(() => import('../components/UploadModal'));

const TAGS = [
  { key: 'all', label: 'Todos' },
  { key: 'fanart', label: 'Fan art' },
  { key: 'scifi', label: 'Sci-fi' },
  { key: 'fantasy', label: 'Fantasy' },
  { key: 'character', label: 'Personagem' },
  { key: 'environment', label: 'Ambiente' },
  { key: 'anime', label: 'Anime' },
  { key: 'realistic', label: 'Realista' }
];

function Home() {
  const navigate = useNavigate();
  const { user, setUser, logout, authFetch, getMediaUrl } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');
  const [activeTag, setActiveTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNSFW, setShowNSFW] = useState(() => localStorage.getItem('show_nsfw') === 'true');
  const accentColor = '#9d7be6';
  const themeMode = 'midnight';
  const isMobileDevice = () => window.innerWidth < 900 || window.matchMedia('(pointer: coarse)').matches;
  const [sidebarHidden, setSidebarHidden] = useState(() => isMobileDevice());
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchKind, setSearchKind] = useState('posts');
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleResults, setPeopleResults] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [mobileTagsExpanded, setMobileTagsExpanded] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [counts, setCounts] = useState({});
  const [activeView, setActiveView] = useState('gallery');
  const activeViewRef = useRef('gallery');
  const [notificationsOpen,setNotificationsOpen]=useState(false);
  const [notificationCount,setNotificationCount]=useState(0);
  const [mobileChromeHidden,setMobileChromeHidden]=useState(false);
  const lastScrollY=useRef(0);
  const pullStartY=useRef(null);
  const pullDistanceRef=useRef(0);
  const [pullDistance,setPullDistance]=useState(0);
  const [refreshNonce,setRefreshNonce]=useState(0);

  // Modals
  const [lightboxPost, setLightboxPost] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(null); // user_id string or null
  const [storefrontOpen, setStorefrontOpen] = useState(null);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('store_connect')) {
      setActiveView('shop');
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', accentColor);
  }, [accentColor]);

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem('show_nsfw', showNSFW.toString());
  }, [showNSFW]);

  useEffect(()=>{authFetch('/api/push/activity',{method:'POST'}).catch(()=>{});},[authFetch]);

  useEffect(()=>{
    const onScroll=()=>{
      if(!isMobileDevice())return;
      setSidebarHidden(true);
      const current=Math.max(0,window.scrollY);
      const delta=current-lastScrollY.current;
      if(current<60)setMobileChromeHidden(false);
      else if(delta>10)setMobileChromeHidden(true);
      else if(delta<-8)setMobileChromeHidden(false);
      lastScrollY.current=current;
    };
    window.addEventListener('scroll',onScroll,{passive:true});
    return()=>window.removeEventListener('scroll',onScroll);
  },[]);

  // Right-click and shortcut protection
  useEffect(() => {
    const preventContext = (e) => e.preventDefault();
    const preventKeys = (e) => {
      if (e.ctrlKey && ['s', 'u', 'i'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', preventKeys);
    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', preventKeys);
    };
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/stats/counts`);
      const data = await response.json();
      setCounts(data);
    } catch (err) {
      console.error(err);
    }
  }, [authFetch]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (searchQuery.trim()) {
        url = `${BACKEND_URL}/api/search?q=${encodeURIComponent(searchQuery)}&type=posts`;
      } else {
        const params = new URLSearchParams({
          show_nsfw: showNSFW,
          media_type: activeType,
          tag: activeTag
        });
        url = `${BACKEND_URL}/api/posts/feed?${params}`;
      }
      const response = await authFetch(url);
      const data = await response.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, activeType, activeTag, showNSFW, searchQuery]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    const t = setTimeout(loadPosts, searchQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadPosts]);

  useEffect(() => {
    if (!mobileSearchOpen || searchKind !== 'users' || peopleQuery.trim().length < 2) {
      setPeopleResults([]);
      return;
    }
    let cancelled=false;
    const timer=setTimeout(async()=>{
      setPeopleLoading(true);
      try {
        const response=await authFetch(`${BACKEND_URL}/api/search?q=${encodeURIComponent(peopleQuery.trim())}&type=users`);
        const data=await response.json();
        if(!cancelled) setPeopleResults(response.ok&&Array.isArray(data)?data:[]);
      } catch (_) { if(!cancelled) setPeopleResults([]); }
      finally { if(!cancelled) setPeopleLoading(false); }
    },300);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[mobileSearchOpen,searchKind,peopleQuery,authFetch]);

  const handleLike = async (postId) => {
    await authFetch(`${BACKEND_URL}/api/posts/${postId}/like`, { method: 'POST' });
  };

  const handleUploadSuccess = (newPost) => {
    setUploadOpen(false);
    loadPosts();
    loadCounts();
  };

  const refreshCurrentView=useCallback(async()=>{
    if(activeViewRef.current==='gallery')await Promise.all([loadPosts(),loadCounts()]);
    else setRefreshNonce(value=>value+1);
  },[loadPosts,loadCounts]);

  const handlePullStart=(event)=>{
    if(!isMobileDevice()||window.scrollY>1||lightboxPost||profileOpen||settingsOpen||uploadOpen)return;
    pullStartY.current=event.touches?.[0]?.clientY??null;
  };
  const handlePullMove=(event)=>{
    if(pullStartY.current===null)return;
    const distance=Math.max(0,(event.touches?.[0]?.clientY||0)-pullStartY.current);
    const nextDistance=Math.min(92,distance*.48);pullDistanceRef.current=nextDistance;setPullDistance(nextDistance);
  };
  const handlePullEnd=()=>{
    const shouldRefresh=pullDistanceRef.current>=58;
    pullStartY.current=null;pullDistanceRef.current=0;setPullDistance(0);
    if(shouldRefresh)refreshCurrentView();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLikeChange = (postId, newLiked) => {
    setPosts(prev => prev.map(p => 
      p.post_id === postId 
        ? { ...p, is_liked: newLiked, likes_count: newLiked ? p.likes_count + 1 : p.likes_count - 1 }
        : p
    ));
  };

  useEffect(()=>{activeViewRef.current=activeView;},[activeView]);

  useEffect(()=>{
    if(!isNativeAndroid())return undefined;
    let listener;
    CapacitorApp.addListener('backButton',()=>{
      if(exitConfirmOpen){setExitConfirmOpen(false);return;}
      if(mobileSearchOpen){setMobileSearchOpen(false);return;}
      if(notificationsOpen){setNotificationsOpen(false);return;}
      if(settingsOpen){setSettingsOpen(false);return;}
      if(profileOpen){setProfileOpen(null);return;}
      if(lightboxPost){setLightboxPost(null);return;}
      if(activeViewRef.current!=='gallery'){
        activeViewRef.current='gallery';setActiveView('gallery');window.scrollTo({top:0});return;
      }
      setExitConfirmOpen(true);
    }).then(handle=>{listener=handle;});
    return()=>listener?.remove();
  },[exitConfirmOpen,mobileSearchOpen,notificationsOpen,settingsOpen,profileOpen,lightboxPost]);

  useEffect(() => {
    const initialState=window.history.state||{};
    if(!initialState.blopRoot&&!initialState.blopGuard){
      window.history.replaceState({...initialState,blopRoot:true},'',window.location.href);
      window.history.pushState({blopGuard:true},'',window.location.href);
    }
    const closeOnBack=()=>{
      setLightboxPost(null);
      setProfileOpen(null);
      setSettingsOpen(false);
      setNotificationsOpen(false);
      setMobileSearchOpen(false);
      if(window.history.state?.blopRoot){
        if(activeViewRef.current!=='gallery'){
          activeViewRef.current='gallery';setActiveView('gallery');
          window.history.pushState({blopGuard:true},'',window.location.href);
        }else {
          setExitConfirmOpen(true);
          window.history.pushState({blopGuard:true},'',window.location.href);
        }
      }
    };
    window.addEventListener('popstate',closeOnBack);
    window.addEventListener('hashchange',closeOnBack);
    return ()=>{
      window.removeEventListener('popstate',closeOnBack);
      window.removeEventListener('hashchange',closeOnBack);
    };
  },[]);

  // O perfil funciona como uma tela no celular: o botão/gesto Voltar deve
  // fechá-lo antes de permitir que o navegador ou aplicativo seja encerrado.
  useEffect(() => {
    if (profileOpen && window.location.hash !== '#perfil') {
      const profileUrl = new URL(window.location.href);
      profileUrl.hash = 'perfil';
      window.history.pushState({blopProfile:true}, '', profileUrl);
    }
  }, [profileOpen]);

  const closeProfile = () => {
    if (window.history.state?.blopProfile || window.location.hash === '#perfil') window.history.back();
    else setProfileOpen(null);
  };

  const openPost = (post) => {
    if (!post) return;
    const url=`${window.location.pathname}?post=${encodeURIComponent(post.post_id)}`;
    if (lightboxPost) window.history.replaceState({blopPost:true},'',url);
    else window.history.pushState({blopPost:true},'',url);
    setLightboxPost(post);
  };

  const openNotificationPost = async (postId) => {
    const existing=posts.find(post=>post.post_id===postId);
    if(existing){openPost(existing);return;}
    const response=await authFetch(`${BACKEND_URL}/api/posts/${postId}`);
    if(response.ok)openPost(await response.json());
  };

  const closePost = () => {
    if (window.history.state?.blopPost) window.history.back();
    else setLightboxPost(null);
  };

  return (
    <div className={`min-h-screen app-shell ${mobileChromeHidden?'mobile-chrome-hidden':''}`} onTouchStart={handlePullStart} onTouchMove={handlePullMove} onTouchEnd={handlePullEnd} onTouchCancel={handlePullEnd}>
      <div className={`pull-refresh ${pullDistance>=58?'ready':''}`} style={{transform:`translate(-50%, ${pullDistance-52}px)`}}><i className={`ti ${pullDistance>=58?'ti-refresh':'ti-arrow-down'}`}/><span>{pullDistance>=58?'Solte para atualizar':'Puxe para atualizar'}</span></div>
      <div className="app-ambient-lines" aria-hidden="true"></div>
      <Navbar
        user={user}
        activeView={activeView}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        onMobileSearch={() => setMobileSearchOpen(true)}
        onNotifications={() => setNotificationsOpen(true)}
        notificationCount={notificationCount}
        onUpload={() => setUploadOpen(true)}
        onOpenProfile={() => setProfileOpen(user?.user_id)}
        onOpenSettings={() => setSettingsOpen(true)}
        onLogout={handleLogout}
        onToggleSidebar={() => {
          if (activeView !== 'gallery') {
            setActiveView('gallery');
            setSidebarHidden(false);
          } else setSidebarHidden(!sidebarHidden);
        }}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        getMediaUrl={getMediaUrl}
      />

      <div className="flex">
        {!sidebarHidden&&isMobileDevice()&&<button className="sidebar-mobile-backdrop" aria-label="Fechar categorias" onClick={()=>setSidebarHidden(true)}/>} 
        {activeView === 'gallery' && <Sidebar
          activeType={activeType}
          onSelectType={(type) => { setActiveType(type); if (isMobileDevice()) setSidebarHidden(true); }}
          counts={counts}
          hidden={sidebarHidden}
          showSensitive={showNSFW}
          onToggleSensitive={() => { setShowNSFW(value => !value); setActiveType('all'); if (isMobileDevice()) setSidebarHidden(true); }}
        />}

        <main className="flex-1 p-3 sm:p-6 min-w-0" data-testid="main-content">
          <div className="view-switch" aria-label="Escolha da página">
            <button className={activeView==='gallery'?'active':''} onClick={()=>setActiveView('gallery')}><i className="ti ti-layout-grid"></i>Galeria</button>
            <button className={activeView==='feed'?'active':''} onClick={()=>setActiveView('feed')}><i className="ti ti-planet"></i>Feed</button>
            <button className={activeView==='shop'?'active':''} onClick={()=>setActiveView('shop')}><i className="ti ti-shopping-bag-heart"></i>Loja</button>
            <button className={activeView==='chat'?'active':''} onClick={()=>setActiveView('chat')}><i className="ti ti-messages"></i>Conversas</button>
          </div>
          {activeView === 'gallery' ? <>
          {/* Filter chips */}
          <div className="desktop-tags flex items-center gap-2 mb-5 flex-wrap" data-testid="filter-chips">
            {TAGS.map(t => {
              const isActive = activeTag === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTag(t.key)}
                  data-testid={`filter-${t.key}`}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium cursor-pointer transition-all ${
                    isActive ? 'text-white' : 'text-white/60 hover:text-white'
                  }`}
                  style={{
                    background: isActive ? 'var(--accent)' : 'var(--glass)',
                    border: isActive ? '1px solid var(--accent)' : '1px solid var(--glass-border)'
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="mobile-tags" aria-label="Filtros rápidos">
            {TAGS.slice(0,mobileTagsExpanded?TAGS.length:3).map(t => <button key={t.key} onClick={() => setActiveTag(t.key)} className={activeTag===t.key?'active':''}>{t.label}</button>)}
            <button className="more" onClick={() => setMobileTagsExpanded(!mobileTagsExpanded)}>{mobileTagsExpanded?'−':`+${TAGS.length-3}`}</button>
          </div>

          {/* Content grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{borderColor:'var(--accent)', borderTopColor:'transparent'}}></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
              <i className="ti ti-photo-off text-5xl text-white/15"></i>
              <p className="text-white/40 text-base">
                {searchQuery ? 'Nenhum resultado encontrado' : 'Nenhuma postagem ainda'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setUploadOpen(true)}
                  data-testid="empty-upload-btn"
                  className="mt-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: 'var(--accent)' }}
                >
                  <i className="ti ti-upload mr-1.5"></i>Publicar a primeira arte
                </button>
              )}
            </div>
          ) : (
            <div className="masonry-grid animate-fade-up" data-testid="posts-grid">
              {posts.filter(p => !p.is_nsfw || showNSFW).map(post => (
                <PostCard
                  key={post.post_id}
                  post={post}
                  onClick={openPost}
                  onLike={handleLike}
                  getMediaUrl={getMediaUrl}
                  onOpenProfile={(id) => id && setProfileOpen(id)}
                />
              ))}
            </div>
          )}
          </> : activeView==='feed' ? <SocialFeed key={`feed-${refreshNonce}`} authFetch={authFetch} user={user} showNSFW={showNSFW} onOpenProfile={(id)=>id&&setProfileOpen(id)} getMediaUrl={getMediaUrl} /> : activeView==='shop'?<Marketplace key={`shop-${refreshNonce}`} authFetch={authFetch} getMediaUrl={getMediaUrl} currentUser={user} onExitStore={()=>{setActiveView('gallery');window.scrollTo({top:0,behavior:'smooth'});}} onOpenProfile={(id)=>id&&setStorefrontOpen(id)}/>:<PrivateChat key={`chat-${refreshNonce}`} authFetch={authFetch} currentUser={user} onOpenProfile={(id)=>id&&setProfileOpen(id)} getMediaUrl={getMediaUrl}/>} 
        </main>
      </div>

      {lightboxPost && (
        <Lightbox
          post={lightboxPost}
          onClose={closePost}
          authFetch={authFetch}
          getMediaUrl={getMediaUrl}
          onLikeChange={handleLikeChange}
          onOpenProfile={(id) => {
            if (!id) return;
            setLightboxPost(null);
            // Aguarda o toque terminar antes de montar o perfil. Assim o mesmo
            // gesto não atinge o fundo do novo modal nos WebViews Android.
            window.setTimeout(() => setProfileOpen(id), 80);
          }}
          onOpenPost={openPost}
          currentUser={user}
          onDeleted={(id)=>{setPosts(old=>old.filter(p=>p.post_id!==id));closePost();}}
          onPostUpdated={(id,changes)=>{setPosts(old=>old.map(p=>p.post_id===id?{...p,...changes}:p));setLightboxPost(old=>old?.post_id===id?{...old,...changes}:old);}}
        />
      )}

      {storefrontOpen&&<PublicStorefront userId={storefrontOpen} currentUserId={user.user_id} authFetch={authFetch} getMediaUrl={getMediaUrl} onClose={()=>setStorefrontOpen(null)} onSocialProfile={(id)=>{setStorefrontOpen(null);setProfileOpen(id);}}/>}

      {uploadOpen && (
        <React.Suspense fallback={<div className="publish-loading">Preparando o ateliê...</div>}>
          <UploadModal
            onClose={() => setUploadOpen(false)}
            onSuccess={handleUploadSuccess}
            authFetch={authFetch}
          />
        </React.Suspense>
      )}

      {settingsOpen && (
        <SettingsModal
          onClose={() => setSettingsOpen(false)}
          onOpenProfile={() => { setSettingsOpen(false); setProfileOpen(user?.user_id); }}
          onLogout={handleLogout}
          currentUser={user}
          authFetch={authFetch}
          getMediaUrl={getMediaUrl}
        />
      )}

      {profileOpen && (
        <ProfileModal
          userId={profileOpen}
          currentUser={user}
          isOwn={profileOpen === user?.user_id}
          onClose={closeProfile}
          authFetch={authFetch}
          getMediaUrl={getMediaUrl}
          onPostClick={openPost}
          onProfileUpdated={setUser}
          onOpenSettings={() => { setProfileOpen(null); setSettingsOpen(true); }}
          onOpenUser={(id) => setProfileOpen(id)}
        />
      )}

      <NotificationCenter open={notificationsOpen} onClose={()=>setNotificationsOpen(false)}
        authFetch={authFetch} getMediaUrl={getMediaUrl} onCount={setNotificationCount}
        onOpenPost={openNotificationPost} onOpenProfile={setProfileOpen}
        onOpenChat={()=>{setActiveView('chat');window.scrollTo({top:0,behavior:'smooth'});}} />

      {activeView!=='shop'&&<MobileDock
        onFeed={() => { setActiveView('feed');setMobileSearchOpen(false);window.scrollTo({top:0,behavior:'smooth'}); }}
        onShop={() => { setActiveView('shop');setMobileSearchOpen(false);window.scrollTo({top:0,behavior:'smooth'}); }}
        onChat={() => { setActiveView('chat');setMobileSearchOpen(false);window.scrollTo({top:0,behavior:'smooth'}); }}
        onUpload={() => setUploadOpen(true)}
        onSearch={() => setMobileSearchOpen(true)}
        onProfile={() => setProfileOpen(user?.user_id)} user={user} getMediaUrl={getMediaUrl}
        activeView={activeView}
      />}
      {mobileSearchOpen && <div className="mobile-search-backdrop" onClick={(e)=>e.target===e.currentTarget&&setMobileSearchOpen(false)}>
        <div className="mobile-search-panel">
          <div className="mobile-search-sheet">
            <i className="ti ti-search"></i><input autoFocus value={searchKind==='posts'?searchQuery:peopleQuery} maxLength={100} onChange={(e)=>searchKind==='posts'?setSearchQuery(e.target.value):setPeopleQuery(e.target.value)} placeholder={searchKind==='posts'?'Buscar obras ou tags...':'Buscar nome ou @ID...'} />
            <button onClick={()=>setMobileSearchOpen(false)}><i className="ti ti-x"></i></button>
          </div>
          <div className="mobile-search-kinds">
            <button className={searchKind==='posts'?'active':''} onClick={()=>setSearchKind('posts')}><i className="ti ti-photo"></i>Obras</button>
            <button className={searchKind==='users'?'active':''} onClick={()=>setSearchKind('users')}><i className="ti ti-users"></i>Pessoas</button>
          </div>
          {searchKind==='users' && <div className="mobile-people-results">
            {peopleQuery.trim().length<2?<p>Digite pelo menos 2 letras para pesquisar pessoas.</p>:peopleLoading?<p>Pesquisando...</p>:peopleResults.length===0?<p>Nenhuma pessoa encontrada.</p>:peopleResults.map(person=><button key={person.user_id} onClick={()=>{setMobileSearchOpen(false);setProfileOpen(person.user_id);}}>
              <span>{person.picture?<img src={userPictureUrl(person.picture,getMediaUrl)} alt=""/>:<i className="ti ti-user"></i>}</span>
              <span><b>{person.name}</b><small>@{person.handle}</small>{person.bio&&<em>{person.bio}</em>}</span><i className="ti ti-chevron-right"></i>
            </button>)}
          </div>}
        </div>
      </div>}
      {exitConfirmOpen && <div className="exit-confirm-backdrop" role="presentation" onClick={(event)=>event.target===event.currentTarget&&setExitConfirmOpen(false)}>
        <section className="exit-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="exit-confirm-title">
          <img src="/blop-mini.png" alt="Mascote BlopBoru triste" />
          <h2 id="exit-confirm-title">Já vai embora?</h2>
          <p>A Blop vai sentir saudade. Quer mesmo sair do aplicativo?</p>
          <div>
            <button type="button" className="stay" onClick={()=>setExitConfirmOpen(false)}>Quero ficar</button>
            <button type="button" className="leave" onClick={()=>{setExitConfirmOpen(false);if(isNativeAndroid())CapacitorApp.exitApp();else window.history.back();}}>Sair do app</button>
          </div>
        </section>
      </div>}
    </div>
  );
}

export default Home;
